const FT_PER_M = 3.28084;

// Algolia fixes sort order per index, so each sort the UI offers is served by
// its own replica. Suffixes match the replica names declared in config/scout.php.
const SORT_REPLICA = {
    price_desc: "price_desc",
    price_asc: "price_asc",
};

const DEFAULT_SORT = "price_desc";

// The query-string keys the filters own. Anything else on the URL — campaign
// tags and the like — is left alone when the state is rewritten.
const FILTER_PARAMS = [
    "q",
    "price",
    "loa",
    "unit",
    "guests",
    "sort",
    "builder",
    "year",
    "destination",
];

// Header height, and where the filter bar parks once it sticks below it — both
// match the classes in _layouts/_header.twig and _includes/_yacht-filters.twig.
const HEADER_HEIGHT = 80;
const STICKY_FILTER_TOP = 119;

// Sentinel the *Filter attributes carry when a yacht has no such spec. See the
// filterString() comment for why a boolean flag can't do this job.
const MISSING_SPEC = -1;

// Separators and currency symbol placement both differ per site — en gives
// €29,500,000, de gives 29.500.000 €. The site language is on <html lang>, so
// this matches the `| currency` output from _includes/_macros/_price.twig.
const locale = document.documentElement.lang || "en";
const currencyFormats = {};

const formatMoney = (value, currency = "EUR") => {
    const code = currency || "EUR";

    if (!currencyFormats[code]) {
        currencyFormats[code] = new Intl.NumberFormat(locale, {
            style: "currency",
            currency: code,
            maximumFractionDigits: 0,
        });
    }

    return currencyFormats[code].format(value);
};

// Plain numbers need the same per-site separators as prices — en 28.06, de 28,06.
// Matches the `num.format()` output from _includes/_macros/_number.twig.
const decimalFormat = new Intl.NumberFormat(locale);
const formatNumber = (value) => decimalFormat.format(value);

/**
 * Filter/paginate a yacht section against Algolia.
 *
 * `section` is "sales" or "charter" — they share sliders, sorting and paging
 * but differ in which filters exist and whether an on-market status applies.
 */
export default ({
    appId,
    apiKey,
    baseIndex,
    section,
    charterFromLabel = "",
    perWeekLabel = "",
}) => ({
    section,
    baseIndex,
    client: null,

    // `ready` gates the filter UI: the slider bounds come from Algolia facet
    // stats, so rendering before they land would flash a 0–0 range.
    ready: false,
    loading: true,
    failed: false,

    hits: [],
    nbHits: 0,
    nbPages: 0,
    currentPage: 1,
    perPage: 12,

    mobileFilterOpen: false,
    advancedOpen: false,
    stuck: false,

    brands: [],
    years: [],
    destinationOptions: [],

    priceAbsMin: 0,
    priceAbsMax: 0,
    loaAbsMinM: 0,
    loaAbsMaxM: 0,
    loaAbsMinFt: 0,
    loaAbsMaxFt: 0,
    loaAbsMin: 0,
    loaAbsMax: 0,
    berthsAbsMin: 0,
    berthsAbsMax: 0,

    filters: {
        name: "",
        priceMin: 0,
        priceMax: 0,
        loaMin: 0,
        loaMax: 0,
        loaUnit: "m",
        sortBy: DEFAULT_SORT,
        guestsMin: 0,
        guestsMax: 0,
        // sales only
        builder: "",
        minYear: "",
        // charter only
        destinations: [],
    },

    searchToken: 0,
    debounceTimer: null,

    // Set while the state is being written back from the URL, so the filter
    // watcher below doesn't mistake a restore for the reader touching a slider.
    restoring: false,

    scrollFrame: null,
    scrollTarget: 0,
    scrollAbandoned: false,
    stopScroll: () => {},

    async init() {
        try {
            this.client = await window.algoliasearch(appId, apiKey);
            await this.loadFacets();
        } catch (error) {
            this.failed = true;
            this.loading = false;
            return;
        }

        this.ready = true;
        this.applyUrlFilters();
        this.currentPage = this.pageFromUrl();

        // Seeding the filters above would otherwise trip these immediately.
        this.$watch(
            () => JSON.stringify(this.filters),
            () => {
                if (this.restoring) return;

                this.currentPage = 1;
                // Replace rather than push: a slider drag would otherwise bury
                // the page the reader arrived on under a dozen history entries.
                this.syncUrl(true);
                this.queueSearch();
            },
        );

        // Back and forward return to states the reader actually navigated to.
        window.addEventListener("popstate", () => this.restoreFromUrl());

        this.$watch("filters.loaUnit", (unit) => {
            const metres = unit !== "ft";

            this.loaAbsMin = metres ? this.loaAbsMinM : this.loaAbsMinFt;
            this.loaAbsMax = metres ? this.loaAbsMaxM : this.loaAbsMaxFt;
            this.filters.loaMin = this.loaAbsMin;
            this.filters.loaMax = this.loaAbsMax;
        });

        // Only our own params count: a campaign link carrying utm tags is still
        // an ordinary visit and should start at the top of the page.
        const params = new URLSearchParams(window.location.search);
        const landed = [...FILTER_PARAMS, "page"].some((key) =>
            params.has(key),
        );

        this.search().then(() => {
            // Someone opening a shared search should see the yachts it picked
            // out, not the hero they never asked for.
            if (landed) this.$nextTick(() => this.scrollToResults(true));
        });
    },

    /**
     * The filters and the page live in the URL, so a search survives a refresh
     * and can be pasted to someone else. Only what differs from the defaults is
     * written: an untouched page keeps a clean URL.
     */
    pageFromUrl() {
        const value = Number(
            new URLSearchParams(window.location.search).get("page"),
        );

        return Number.isInteger(value) && value > 1 ? value : 1;
    },

    syncUrl(replace = false) {
        const url = new URL(window.location.href);

        // Cleared and rewritten in a fixed order, so paging twice doesn't
        // reshuffle the query string under the reader.
        [...FILTER_PARAMS, "page"].forEach((key) =>
            url.searchParams.delete(key),
        );
        this.writeFilterParams(url.searchParams);

        if (this.currentPage > 1) {
            url.searchParams.set("page", this.currentPage);
        }

        if (url.href === window.location.href) return;

        history[replace ? "replaceState" : "pushState"]({}, "", url.href);
    },

    writeFilterParams(params) {
        const f = this.filters;

        if (f.name) params.set("q", f.name);
        if (f.sortBy !== DEFAULT_SORT) params.set("sort", f.sortBy);
        if (f.loaUnit !== "m") params.set("unit", f.loaUnit);

        // A slider still at its bounds is filtering nothing, so it stays off
        // the URL — the same test filterString() uses to skip a clause.
        if (f.priceMin > this.priceAbsMin || f.priceMax < this.priceAbsMax) {
            params.set("price", `${f.priceMin}-${f.priceMax}`);
        }

        if (f.loaMin > this.loaAbsMin || f.loaMax < this.loaAbsMax) {
            params.set("loa", `${f.loaMin}-${f.loaMax}`);
        }

        if (f.guestsMin > this.berthsAbsMin || f.guestsMax < this.berthsAbsMax) {
            params.set("guests", `${f.guestsMin}-${f.guestsMax}`);
        }

        if (this.section === "sales") {
            if (f.builder) params.set("builder", f.builder);
            if (f.minYear) params.set("year", f.minYear);
        } else {
            // Repeated keys rather than one joined value: destination names
            // carry commas of their own.
            f.destinations.forEach((d) => params.append("destination", d));
        }
    },

    /**
     * Runs once the facet bounds are known, so every value can be checked
     * against what the filters actually offer — a hand-edited or stale link
     * must not leave a slider handle off its track or a dropdown on a builder
     * that no longer has yachts.
     */
    applyUrlFilters() {
        const params = new URLSearchParams(window.location.search);
        const f = this.filters;

        const name = params.get("q");
        if (name) f.name = name;

        const sort = params.get("sort");
        if (sort && SORT_REPLICA[sort]) f.sortBy = sort;

        // The unit decides which bounds the length range is measured against,
        // so it has to be restored before the range itself.
        if (params.get("unit") === "ft") {
            f.loaUnit = "ft";
            this.loaAbsMin = this.loaAbsMinFt;
            this.loaAbsMax = this.loaAbsMaxFt;
            f.loaMin = this.loaAbsMin;
            f.loaMax = this.loaAbsMax;
        }

        const price = this.rangeParam(
            params.get("price"),
            this.priceAbsMin,
            this.priceAbsMax,
        );
        if (price) [f.priceMin, f.priceMax] = price;

        const loa = this.rangeParam(
            params.get("loa"),
            this.loaAbsMin,
            this.loaAbsMax,
        );
        if (loa) [f.loaMin, f.loaMax] = loa;

        const guests = this.rangeParam(
            params.get("guests"),
            this.berthsAbsMin,
            this.berthsAbsMax,
        );
        if (guests) [f.guestsMin, f.guestsMax] = guests;

        if (this.section === "sales") {
            const builder = params.get("builder");
            if (this.brands.includes(builder)) f.builder = builder;

            const year = params.get("year");
            if (this.years.includes(Number(year))) f.minYear = year;
        } else {
            const destinations = params
                .getAll("destination")
                .filter((d) => this.destinationOptions.includes(d));

            if (destinations.length) f.destinations = destinations;
        }
    },

    rangeParam(value, absMin, absMax) {
        // Strict "min-max": a malformed range is dropped rather than guessed
        // at, since a partial read would move a handle the reader never did.
        if (!value || !/^\d+(\.\d+)?-\d+(\.\d+)?$/.test(value)) return null;

        const [min, max] = value.split("-").map(Number);

        if (min > max) return null;

        const clamp = (n) => Math.min(Math.max(n, absMin), absMax);

        return [clamp(min), clamp(max)];
    },

    /**
     * Back and forward hand us a whole state, not a delta, so the filters go
     * back to their defaults before the URL is read over the top of them.
     */
    restoreFromUrl() {
        this.restoring = true;

        this.resetFilters();
        this.applyUrlFilters();
        this.currentPage = this.pageFromUrl();

        this.$nextTick(() => {
            this.restoring = false;
        });

        this.search().then(() => this.$nextTick(() => this.scrollToResults()));
    },

    resetFilters() {
        this.loaAbsMin = this.loaAbsMinM;
        this.loaAbsMax = this.loaAbsMaxM;

        Object.assign(this.filters, {
            name: "",
            sortBy: DEFAULT_SORT,
            loaUnit: "m",
            priceMin: this.priceAbsMin,
            priceMax: this.priceAbsMax,
            loaMin: this.loaAbsMinM,
            loaMax: this.loaAbsMaxM,
            guestsMin: this.berthsAbsMin,
            guestsMax: this.berthsAbsMax,
            builder: "",
            minYear: "",
            destinations: [],
        });
    },

    /**
     * Sort order and the length unit are display preferences rather than
     * filters, so neither counts here nor is undone by clearAll().
     */
    get hasActiveFilters() {
        const f = this.filters;

        return Boolean(
            f.name ||
                f.builder ||
                f.minYear ||
                f.destinations.length ||
                f.priceMin > this.priceAbsMin ||
                f.priceMax < this.priceAbsMax ||
                f.loaMin > this.loaAbsMin ||
                f.loaMax < this.loaAbsMax ||
                f.guestsMin > this.berthsAbsMin ||
                f.guestsMax < this.berthsAbsMax,
        );
    },

    clearAll() {
        Object.assign(this.filters, {
            name: "",
            priceMin: this.priceAbsMin,
            priceMax: this.priceAbsMax,
            loaMin: this.loaAbsMin,
            loaMax: this.loaAbsMax,
            guestsMin: this.berthsAbsMin,
            guestsMax: this.berthsAbsMax,
            builder: "",
            minYear: "",
            destinations: [],
        });
    },

    /**
     * One empty-query call supplies every dropdown option and slider bound,
     * which is why the page ships no yacht data of its own.
     */
    async loadFacets() {
        const facets = ["price", "loaMeters", "berths", "buildYear"];
        facets.push(this.section === "sales" ? "brand" : "destinations");

        const { results } = await this.client.search([
            {
                indexName: this.baseIndex,
                query: "",
                filters: this.baseFilter(),
                hitsPerPage: 0,
                facets,
                maxValuesPerFacet: 1000,
            },
        ]);

        const stats = results[0].facets_stats || {};
        const values = results[0].facets || {};

        const price = stats.price || { min: 0, max: 0 };
        this.priceAbsMin = Math.floor(price.min);
        this.priceAbsMax = Math.ceil(price.max);

        const loa = stats.loaMeters || { min: 0, max: 0 };
        this.loaAbsMinM = Math.floor(loa.min);
        this.loaAbsMaxM = Math.ceil(loa.max);
        this.loaAbsMinFt = Math.round(this.loaAbsMinM * FT_PER_M);
        this.loaAbsMaxFt = Math.round(this.loaAbsMaxM * FT_PER_M);
        this.loaAbsMin = this.loaAbsMinM;
        this.loaAbsMax = this.loaAbsMaxM;

        const berths = stats.berths || { min: 0, max: 0 };
        this.berthsAbsMin = Math.floor(berths.min);
        this.berthsAbsMax = Math.ceil(berths.max);

        this.brands = Object.keys(values.brand || {}).sort((a, b) =>
            a.localeCompare(b),
        );
        this.destinationOptions = Object.keys(values.destinations || {}).sort(
            (a, b) => a.localeCompare(b),
        );
        this.years = Object.keys(values.buildYear || {})
            .map(Number)
            .sort((a, b) => a - b);

        this.resetFilters();
    },

    baseFilter() {
        return this.section === "sales"
            ? "salesStatus:onmarket AND sold:false"
            : "";
    },

    /**
     * A missing spec must not exclude a yacht — a POA one stays visible whatever
     * the price slider says. Algolia drops records lacking an attribute from a
     * numeric filter and rejects `boolean OR numeric range`, so the *Filter
     * attributes carry MISSING_SPEC and keep both sides of the OR numeric. A
     * slider still at its bounds contributes no clause at all.
     */
    filterString() {
        const f = this.filters;
        const clauses = [];
        const base = this.baseFilter();

        if (base) clauses.push(base);

        if (f.priceMin > this.priceAbsMin || f.priceMax < this.priceAbsMax) {
            clauses.push(this.range("priceFilter", f.priceMin, f.priceMax));
        }

        if (f.loaMin > this.loaAbsMin || f.loaMax < this.loaAbsMax) {
            clauses.push(
                this.range(
                    "loaMetersFilter",
                    this.toMeters(f.loaMin),
                    this.toMeters(f.loaMax),
                ),
            );
        }

        if (f.guestsMin > this.berthsAbsMin || f.guestsMax < this.berthsAbsMax) {
            clauses.push(this.range("berthsFilter", f.guestsMin, f.guestsMax));
        }

        if (this.section === "sales") {
            if (f.builder) clauses.push(`brand:${JSON.stringify(f.builder)}`);
            // A yacht with no build year is excluded once a minimum is set.
            if (f.minYear) clauses.push(`buildYear >= ${f.minYear}`);
        } else {
            if (f.destinations.length) {
                const any = f.destinations
                    .map((d) => `destinations:${JSON.stringify(d)}`)
                    .join(" OR ");
                clauses.push(`(${any})`);
            }
        }

        return clauses.join(" AND ");
    },

    range(attribute, min, max) {
        return `(${attribute} = ${MISSING_SPEC} OR ${attribute}:${min} TO ${max})`;
    },

    toMeters(value) {
        return this.filters.loaUnit === "ft"
            ? Math.round((value / FT_PER_M) * 10) / 10
            : value;
    },

    indexName() {
        const replica = SORT_REPLICA[this.filters.sortBy];

        return replica ? `${this.baseIndex}_${replica}` : this.baseIndex;
    },

    queueSearch() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.search(), 250);
    },

    async search() {
        if (!this.client) return;

        // Sliders fire fast enough that responses can land out of order.
        const token = ++this.searchToken;

        this.loading = true;

        try {
            const { results } = await this.client.search([
                {
                    indexName: this.indexName(),
                    query: this.filters.name,
                    filters: this.filterString(),
                    // The box is labelled by yacht name, and sorting runs on a
                    // replica that ignores textual relevance, so a description
                    // match would land in price order among unrelated yachts.
                    // Brand stays searchable: charter has no builder dropdown.
                    restrictSearchableAttributes: [
                        "title",
                        "yachtName",
                        "brand",
                    ],
                    page: this.currentPage - 1,
                    hitsPerPage: this.perPage,
                },
            ]);

            if (token !== this.searchToken) return;

            this.hits = results[0].hits;
            this.nbHits = results[0].nbHits;
            this.nbPages = results[0].nbPages;
            this.failed = false;

            // A shared or bookmarked ?page= can outlive the results it pointed
            // at. Algolia answers an overshoot with nbHits 0 and no page count
            // to clamp against, so fall back to the first page — an empty grid
            // on page 1 is a genuine no-match and stays put.
            if (this.nbHits === 0 && this.currentPage > 1) {
                this.currentPage = 1;
                this.syncUrl(true);

                return this.search();
            }
        } catch (error) {
            if (token !== this.searchToken) return;

            this.hits = [];
            this.nbHits = 0;
            this.nbPages = 0;
            this.failed = true;
        } finally {
            if (token === this.searchToken) this.loading = false;
        }
    },

    goToPage(page) {
        if (page < 1 || page > this.nbPages || page === this.currentPage) return;

        this.currentPage = page;
        this.syncUrl();

        const search = this.search();

        // Start at once so the click feels answered, then re-aim once the new
        // hits are in the DOM: the sticky bar changes height as it settles, so
        // the resting place moves a little under the animation.
        this.scrollAbandoned = false;
        this.scrollToResults();
        search.then(() =>
            this.$nextTick(() => {
                if (!this.scrollAbandoned) this.scrollToResults();
            }),
        );
    },

    /**
     * Bring the first row of results into view. The site header floats over the
     * page and the filter bar sticks under it, so the grid has to stop clear of
     * both. Never scrolls down unless `force` is set — re-running this after a
     * page change must not drag a reader back.
     */
    scrollToResults(force = false) {
        const grid = this.$refs.results;

        if (!grid) {
            this.animateScrollTo(0);
            return;
        }

        const filters = this.$refs.filters;
        const sticky =
            filters && getComputedStyle(filters).position === "sticky";
        const offset = sticky
            ? STICKY_FILTER_TOP + filters.offsetHeight
            : HEADER_HEIGHT;
        const top = Math.max(
            grid.getBoundingClientRect().top + window.scrollY - offset,
            0,
        );

        if (!force && window.scrollY <= top + 1) return;

        this.animateScrollTo(top);
    },

    /**
     * Driven frame by frame rather than by `behavior: "smooth"`. Replacing the
     * grid mid-flight lets the browser abandon a native smooth scroll, which
     * showed up as a page change that set off and then stopped halfway; setting
     * the position each frame simply overrides that. Any real input from the
     * reader still wins, and a re-aim within a few pixels is left alone so the
     * second call of a page change doesn't restart the easing.
     */
    animateScrollTo(top) {
        const distance = top - window.scrollY;

        if (this.scrollFrame && Math.abs(top - this.scrollTarget) <= 4) return;

        this.stopScroll();
        this.scrollTarget = top;

        if (
            Math.abs(distance) < 2 ||
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
            window.scrollTo(0, top);
            return;
        }

        const start = window.scrollY;
        const startedAt = performance.now();
        const duration = 500;

        // Once the reader takes over, stay out of the way: the re-aim that
        // follows the next set of hits must not grab the page back.
        const abandon = () => {
            this.scrollAbandoned = true;
            this.stopScroll();
        };

        this.stopScroll = () => {
            cancelAnimationFrame(this.scrollFrame);
            this.scrollFrame = null;
            ["wheel", "touchstart", "keydown"].forEach((event) =>
                window.removeEventListener(event, abandon),
            );
            this.stopScroll = () => {};
        };

        ["wheel", "touchstart", "keydown"].forEach((event) =>
            window.addEventListener(event, abandon, { passive: true }),
        );

        const step = (now) => {
            const progress = Math.min((now - startedAt) / duration, 1);
            // easeOutCubic: quick off the mark, gentle into place.
            const eased = 1 - Math.pow(1 - progress, 3);

            // The grid re-rendering can change the page height mid-animation.
            const limit = Math.max(
                document.documentElement.scrollHeight - window.innerHeight,
                0,
            );

            window.scrollTo(0, Math.min(start + distance * eased, limit));

            if (progress < 1) {
                this.scrollFrame = requestAnimationFrame(step);
                return;
            }

            this.stopScroll();
        };

        this.scrollFrame = requestAnimationFrame(step);
    },

    prevPage() {
        this.goToPage(this.currentPage - 1);
    },

    nextPage() {
        this.goToPage(this.currentPage + 1);
    },

    rangePercent(val, min, max) {
        if (max === min) return 0;

        return Math.max(0, Math.min(100, ((val - min) / (max - min)) * 100));
    },

    // Slider bounds come from the indexed price, which is always EUR.
    formatCurrency(val) {
        return formatMoney(Math.round(val));
    },

    displayPrice(yacht) {
        if (!yacht.price || parseFloat(yacht.price) <= 0) return "POA";

        // `currency` is the ISO code indexed by config/scout.php.
        const amount = formatMoney(parseFloat(yacht.price), yacht.currency);

        // Charter rates are a weekly 'from' figure; a sales price is not.
        const isCharter = this.section === "charter";
        const prefix = isCharter && charterFromLabel ? charterFromLabel + " " : "";
        const suffix = isCharter && perWeekLabel ? " " + perWeekLabel : "";

        return prefix + amount + suffix;
    },

    // One row per cruising area, collapsed to a single "Location" row when a
    // yacht stays in the same place all year.
    cruisingAreas(yacht) {
        const summer = yacht.areaSummer || "";
        const winter = yacht.areaWinter || "";

        if (summer && winter && summer === winter) {
            return [{ icon: "summerWinter", label: summer }];
        }

        const rows = [];
        if (summer) rows.push({ icon: "summer", label: summer });
        if (winter) rows.push({ icon: "winter", label: winter });

        return rows;
    },

    displayLoa(yacht) {
        if (!yacht.loaMeters) return "";

        const m = parseFloat(yacht.loaMeters);

        return (
            formatNumber(m) + "m / " + formatNumber(Math.round(m * FT_PER_M)) + "ft"
        );
    },

    listItem(yacht) {
        return {
            id: yacht.id,
            type: yacht.type,
            title: yacht.title,
            yachtUid: yacht.yachtUid,
            projectNumber: yacht.projectNumber,
            thumbnail: yacht.thumbnail,
            price: yacht.price,
            currency: yacht.currency,
            currencySymbol: yacht.currencySymbol,
            url: yacht.url,
        };
    },
});
